import React, { useState, useEffect } from "react";
import axios from "axios";
import { 
  Box, 
  Typography, 
  TextField, 
  Grid, 
  Card, 
  CardContent, 
  Avatar, 
  InputAdornment, 
  ToggleButton, 
  ToggleButtonGroup,
  CircularProgress,
  Alert,
  alpha,
  Divider
} from "@mui/material";
import { 
  Search as SearchIcon, 
  GridView as GridViewIcon, 
  List as ListIcon 
} from "@mui/icons-material";
import { useParams } from "react-router-dom";

const GOLD = "#D4AF37";

const Membergrp = () => {
  const { id } = useParams();
  const [members, setMembers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [view, setView] = useState("grid");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const token = localStorage.getItem("jwttoken");
  const BASE_URL = "https://tdtlworld.com/dss-kaas-backend";

  useEffect(() => {
    const fetchMembers = async () => {
      try {
        const response = await axios.get(
          `${BASE_URL}/socialapp/groups/${id}/`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setMembers(response.data.members || []);
      } catch (error) {
        setError("Failed to fetch members");
      } finally {
        setLoading(false);
      }
    };

    fetchMembers();
  }, [id, token]);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleViewChange = (event, nextView) => {
    if (nextView !== null) {
      setView(nextView);
    }
  };

  const filteredMembers = members.filter(
    (member) =>
      member.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.last_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress sx={{ color: GOLD }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Search and View Controls */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' }, 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        gap: 2, 
        mb: 4 
      }}>
        <TextField
          fullWidth
          placeholder="Search Members..."
          value={searchTerm}
          onChange={handleSearchChange}
          sx={{ maxWidth: { sm: 400 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: GOLD }} />
              </InputAdornment>
            ),
          }}
        />

        <ToggleButtonGroup
          value={view}
          exclusive
          onChange={handleViewChange}
          aria-label="view toggle"
          sx={{
            '& .MuiToggleButton-root': {
              color: 'text.secondary',
              border: `1px solid ${alpha(GOLD, 0.2)}`,
              '&.Mui-selected': {
                bgcolor: alpha(GOLD, 0.1),
                color: GOLD,
              }
            }
          }}
        >
          <ToggleButton value="grid" aria-label="grid view">
            <GridViewIcon />
          </ToggleButton>
          <ToggleButton value="list" aria-label="list view">
            <ListIcon />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Member Section Title */}
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, fontFamily: "'Outfit', sans-serif" }}>
        Members ({filteredMembers.length})
      </Typography>

      {filteredMembers.length > 0 ? (
        <Grid container spacing={3}>
          {filteredMembers.map((member, index) => {
            const profilePic = member.profile_pic && member.profile_pic.trim() !== "" 
              ? member.profile_pic 
              : null;

            return (
              <Grid item key={index} xs={12} sm={view === 'grid' ? 6 : 12} md={view === 'grid' ? 3 : 12}>
                <Card 
                  sx={{ 
                    height: '100%',
                    display: 'flex',
                    flexDirection: view === 'grid' ? 'column' : 'row',
                    alignItems: 'center',
                    p: 2,
                    transition: 'all 0.3s ease',
                    border: `1px solid ${alpha(GOLD, 0.1)}`,
                    '&:hover': {
                      borderColor: GOLD,
                      transform: 'translateY(-5px)',
                      boxShadow: '0 8px 25px rgba(212, 175, 55, 0.15)'
                    }
                  }}
                >
                  <Box sx={{ position: 'relative' }}>
                    <Avatar
                      src={profilePic}
                      sx={{ 
                        width: view === 'grid' ? 80 : 60, 
                        height: view === 'grid' ? 80 : 60, 
                        bgcolor: alpha(GOLD, 0.1),
                        color: GOLD,
                        fontWeight: 700,
                        fontSize: '1.5rem',
                        borderRadius: '16px'
                      }}
                    >
                      {member.first_name.charAt(0)}{member.last_name?.charAt(0)}
                    </Avatar>
                    <Box 
                      sx={{ 
                        position: 'absolute', 
                        bottom: 0, 
                        right: 0, 
                        width: 14, 
                        height: 14, 
                        bgcolor: '#4caf50', 
                        borderRadius: '50%',
                        border: '2px solid white'
                      }} 
                    />
                  </Box>

                  <CardContent sx={{ 
                    flex: 1, 
                    textAlign: view === 'grid' ? 'center' : 'left',
                    pl: view === 'grid' ? 1.5 : 3
                  }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, fontFamily: "'Outfit', sans-serif" }}>
                      {member.first_name} {member.last_name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Joined 5 weeks ago • Active now
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      ) : (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary">
            No members found matching your search.
          </Typography>
        </Box>
      )}

      {/* Footer Meta */}
      <Divider sx={{ my: 4, opacity: 0.5 }} />
      <Typography variant="body2" color="text.secondary" align="center">
        Showing all members of the community group
      </Typography>
    </Box>
  );
};

export default Membergrp;

